import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, FileUp } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Input';
import { toast } from '@/components/ui/Toast';
import { invoicesApi, type ImportResult } from '@/services/endpoints/transactions';
import { queryKeys } from '@/services/queryKeys';
import { ApiError } from '@/services/apiClient';

/**
 * CSV import, with validate-first as the default path.
 *
 * The backend refuses a file with any invalid row unless told otherwise, so
 * the UI leads with a dry run: the user sees exactly which rows are wrong
 * before anything is written.
 */
export function InvoiceImportModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [file, setFile] = useState<File | null>(null);
  const [allowPartial, setAllowPartial] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  const reset = () => {
    setFile(null);
    setAllowPartial(false);
    setResult(null);
  };

  const runImport = useMutation({
    mutationFn: (dryRun: boolean) => invoicesApi.import(file!, { dryRun, allowPartial }),
    onSuccess: (data) => {
      setResult(data);

      if (data.dryRun) {
        if (data.errors.length === 0) toast.success(t('invoice.import.dryRunSuccess', { count: data.validRows }));
        return;
      }

      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      toast.success(t('invoice.import.success', { count: data.imported }));
    },
    onError: (error) => {
      // A rejected all-or-nothing import still carries the per-row errors, so
      // surface them rather than just the summary message.
      if (error instanceof ApiError) {
        const details = error.details as { errors?: ImportResult['errors'] } | undefined;
        if (details?.errors) {
          setResult({
            totalRows: 0,
            validRows: 0,
            imported: 0,
            skipped: details.errors.length,
            dryRun: false,
            errors: details.errors,
          });
        }
        toast.error(error.message);
      } else {
        toast.error(t('errors.unknown'));
      }
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('invoice.import.title')}
      description={t('invoice.import.help')}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={runImport.isPending}>
            {t('common.close')}
          </Button>
          <Button
            variant="outline"
            onClick={() => runImport.mutate(true)}
            isLoading={runImport.isPending && runImport.variables === true}
            disabled={!file || runImport.isPending}
          >
            {t('invoice.import.dryRun')}
          </Button>
          <Button
            onClick={() => runImport.mutate(false)}
            isLoading={runImport.isPending && runImport.variables === false}
            disabled={!file || runImport.isPending}
          >
            {t('common.import')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <label className="flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed border-slate-300 px-6 py-8 text-center hover:border-brand-400 hover:bg-brand-50/40">
          <FileUp className="h-8 w-8 text-slate-400" />
          <span className="text-sm font-medium text-slate-700">
            {file ? file.name : t('invoice.import.selectFile')}
          </span>
          {file && <span className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} KB</span>}
          <input
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(event) => {
              setFile(event.target.files?.[0] ?? null);
              setResult(null);
            }}
          />
        </label>

        <Checkbox
          label={t('invoice.import.allowPartial')}
          checked={allowPartial}
          onChange={(event) => setAllowPartial(event.target.checked)}
        />

        <p className="text-xs text-slate-500">{t('invoice.import.allOrNothing')}</p>

        {result && (
          <div className="rounded-lg border border-slate-200">
            <div className="grid grid-cols-4 divide-x divide-slate-200 border-b border-slate-200">
              {[
                { label: t('invoice.import.totalRows'), value: result.totalRows },
                { label: t('invoice.import.validRows'), value: result.validRows },
                { label: t('invoice.import.imported'), value: result.imported },
                { label: t('invoice.import.skipped'), value: result.skipped },
              ].map((tile) => (
                <div key={tile.label} className="px-3 py-2 text-center">
                  <p className="tabular text-lg font-semibold text-slate-900">{tile.value}</p>
                  <p className="text-[11px] text-slate-500">{tile.label}</p>
                </div>
              ))}
            </div>

            {result.errors.length > 0 && (
              <div className="max-h-56 overflow-y-auto p-3">
                <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-red-700">
                  <AlertTriangle className="h-4 w-4" />
                  {t('invoice.import.rowErrors')}
                </p>
                <ul className="space-y-1.5">
                  {result.errors.map((rowError) => (
                    <li key={rowError.row} className="text-xs">
                      <span className="font-medium text-slate-700">
                        {t('invoice.import.row', { row: rowError.row })}
                        {rowError.invoiceNo ? ` · ${rowError.invoiceNo}` : ''}
                      </span>
                      <span className="text-red-600"> — {rowError.errors.join('; ')}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
